// =====================================================================
// nen.ts — gói dữ liệu nền của hệ thống thật (rpc_tai_nen — migration 0013), dạng CỘT NÉN: mỗi khối {cols, rows, xoa}.
// Cột ĐẦU = khoá dòng (id; riêng cau_hinh là khoa). Cache IndexedDB lưu nguyên dạng nén; bung ra object khi dùng.
//
// Vá delta: dòng mới / đổi ⇒ thay theo khoá; `xoa` ⇒ bỏ. Rồi CẮT PHẠM VI theo mốc lưu trữ của server (lớp có mốc KG
// trước mốc ⇒ rời nền, kéo theo dòng con) — vì mốc trôi theo ngày, lớp ra khỏi nền mà không ai sửa cũng phải rời cache.
// Cuối cùng so số dòng từng khối với server: lệch (vd dòng bị dọn hẳn khỏi thùng rác) ⇒ cache không tin được ⇒ tải lại cả bộ.
// Khối không đổi giữ NGUYÊN object (useMemo phía sau không tính lại).
// =====================================================================

export interface KhoiNen {
  cols: string[];
  rows: unknown[][];
  xoa?: unknown[];
}

export interface GoiNen {
  /** giờ máy chủ lúc chụp (ISO) — mốc cho lần delta sau */
  luc: string;
  /** 'YYYY-MM-DD' theo giờ VN phía máy chủ */
  hom_nay: string;
  /** lớp có mốc KG (KG chính thức, không có thì KG sớm nhất) TRƯỚC ngày này thuộc lưu trữ */
  moc_luu_tru: string;
  may_chu_ms: number;
  bang: Record<string, KhoiNen>;
  so_dong: Record<string, number>;
}

/** Khối con của lớp: cột `lop_id` ⇒ lớp rời nền thì dòng con rời theo. */
// lop_nguon (bước ghép G, khoá = lop_id): mốc ngày + sĩ số phía sheet / admin2 của làn D.
// tac_vu_tick (F, 0300): lượt tick tác vụ — thêm luật cửa sổ ngày ở catPhamVi.
export const KHOI_CON_CUA_LOP = ["lich_hoc", "dieu_chinh", "nghi_xong", "lop_nguon", "tac_vu_tick"] as const;

/** Đầu cửa sổ lượt tick (F — QĐ-H117): hôm nay − cau_hinh.tac_vu_cua_so_ngay (kẹp 7..400, mặc định 90 — CÙNG luật SQL 0300). */
export function dauCuaSoTick(goi: Pick<GoiNen, "bang" | "hom_nay">): string {
  const n = Number(docCauHinh(goi, "tac_vu_cua_so_ngay", 90));
  const cs = Math.max(7, Math.min(400, Number.isFinite(n) && n > 0 ? Math.trunc(n) : 90));
  const d = new Date(goi.hom_nay + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() - cs);
  return d.toISOString().slice(0, 10);
}

export function bung<T = Record<string, unknown>>(k: KhoiNen | undefined): T[] {
  if (!k) return [];
  const { cols, rows } = k;
  const out: T[] = new Array(rows.length);
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const o: Record<string, unknown> = {};
    for (let j = 0; j < cols.length; j++) o[cols[j]] = r[j];
    out[i] = o as T;
  }
  return out;
}

const cungCot = (a: KhoiNen, b: KhoiNen) => a.cols.length === b.cols.length && a.cols.every((c, i) => c === b.cols[i]);

/** Vá 1 khối: dòng theo khoá cột đầu; `xoa` bỏ. Không có gì đổi ⇒ trả NGUYÊN khối cũ. */
export function vaKhoi(a: KhoiNen, b: KhoiNen): KhoiNen {
  const xoa = b.xoa ?? [];
  if (!b.rows.length && !xoa.length) return a;
  // Cột server khác thứ tự cột cache (2 phiên bản lệch nhau) ⇒ ánh xạ theo TÊN; cột lạ ⇒ null.
  const map = cungCot(a, b) ? null : a.cols.map((c) => b.cols.indexOf(c));
  const rows = a.rows.slice();
  const viTri = new Map<string, number>();
  rows.forEach((r, i) => viTri.set(String(r[0]), i));
  for (const r0 of b.rows) {
    const r = map ? map.map((j) => (j < 0 ? null : r0[j])) : r0;
    const i = viTri.get(String(r[0]));
    if (i === undefined) { viTri.set(String(r[0]), rows.length); rows.push(r); } else rows[i] = r;
  }
  const bo = new Set(xoa.map(String));
  return { cols: a.cols, rows: bo.size ? rows.filter((r) => !bo.has(String(r[0]))) : rows };
}

/** Lớp có mốc KG < moc ⇒ rời nền; dòng con của lớp không còn trong nền ⇒ rời theo. Không đổi ⇒ giữ nguyên object. */
export function catPhamVi(goi: GoiNen, moc: string): GoiNen {
  const lop = goi.bang.lop;
  if (!lop) return goi;
  const iCt = lop.cols.indexOf("kg_chinh_thuc"), iSn = lop.cols.indexOf("kg_som_nhat");
  const giu = lop.rows.filter((r) => {
    const neo = (iCt >= 0 ? r[iCt] : null) || (iSn >= 0 ? r[iSn] : null);
    return !neo || String(neo) >= moc;
  });
  const ids = new Set(giu.map((r) => String(r[0])));
  let doi = giu.length !== lop.rows.length;
  const bang: Record<string, KhoiNen> = { ...goi.bang, lop: doi ? { cols: lop.cols, rows: giu } : lop };
  const dauTick = goi.bang.tac_vu_tick && goi.hom_nay ? dauCuaSoTick(goi) : "";
  for (const ten of KHOI_CON_CUA_LOP) {
    const k = goi.bang[ten];
    if (!k) continue;
    const iLop = k.cols.indexOf("lop_id");
    if (iLop < 0) continue;
    // Lượt tick: cửa sổ trôi theo ngày ⇒ dòng có hạn rơi khỏi cửa sổ rời cache (máy chủ đếm so_dong cùng luật).
    const iHan = ten === "tac_vu_tick" ? k.cols.indexOf("han") : -1;
    const con = k.rows.filter((r) => ids.has(String(r[iLop])) && (iHan < 0 || String(r[iHan]) >= dauTick));
    if (con.length !== k.rows.length) { bang[ten] = { cols: k.cols, rows: con }; doi = true; }
  }
  // 0361: tác vụ khác của GV — cửa sổ trôi theo ngày: ca TRƯỚC hôm nay rời cache (máy chủ đếm so_dong cùng luật: ngay >= hôm nay).
  const tv = goi.bang.tac_vu_gv;
  if (tv && goi.hom_nay) {
    const iNg = tv.cols.indexOf("ngay");
    const con = iNg < 0 ? tv.rows : tv.rows.filter((r) => String(r[iNg]) >= goi.hom_nay);
    if (con.length !== tv.rows.length) { bang.tac_vu_gv = { cols: tv.cols, rows: con }; doi = true; }
  }
  return doi ? { ...goi, bang } : goi;
}

/**
 * Vá kết quả delta vào cache. khop=false ⇒ cache không còn tin được (số dòng lệch server / khối mới / cột đổi tên)
 * ⇒ người gọi tải lại cả bộ. soDongDoi = số dòng thêm / sửa / bỏ (để đo).
 */
export function gopDeltaNen(cu: GoiNen, moi: GoiNen): { goi: GoiNen; khop: boolean; soDongDoi: number } {
  let khop = true;
  let soDongDoi = 0;
  const bang: Record<string, KhoiNen> = {};
  for (const ten of new Set([...Object.keys(cu.bang), ...Object.keys(moi.bang)])) {
    const a = cu.bang[ten], b = moi.bang[ten];
    if (!a) { khop = false; if (b) bang[ten] = { cols: b.cols, rows: b.rows }; continue; } // khối mới ⇒ cần cả bộ
    if (!b) { bang[ten] = a; continue; }
    if (!b.cols.every((c) => a.cols.includes(c)) || !a.cols.every((c) => b.cols.includes(c))) khop = false; // đổi cột
    soDongDoi += b.rows.length + (b.xoa?.length ?? 0);
    bang[ten] = vaKhoi(a, b);
  }
  const goi = catPhamVi({ ...cu, luc: moi.luc, hom_nay: moi.hom_nay, moc_luu_tru: moi.moc_luu_tru, may_chu_ms: moi.may_chu_ms, bang, so_dong: moi.so_dong }, moi.moc_luu_tru);
  for (const [ten, n] of Object.entries(moi.so_dong)) {
    if ((goi.bang[ten]?.rows.length ?? 0) !== n) khop = false;
  }
  return { goi, khop, soDongDoi };
}

/**
 * Vá MỘT PHẦN vào gói đang có — kết quả hàm ghi rpc_* (cùng dạng khối) hoặc dòng optimistic.
 * Không đổi luc / so_dong (lần delta sau lấy lại các dòng này — vá trùng không sao).
 */
export function vaGoi<G extends { bang: Record<string, KhoiNen> }>(goi: G, phan: Record<string, KhoiNen | undefined>): G {
  let bang: Record<string, KhoiNen> | null = null;
  for (const [ten, b] of Object.entries(phan)) {
    const a = goi.bang[ten];
    if (!a || !b) continue;
    const moi = vaKhoi(a, b);
    if (moi !== a) { bang ??= { ...goi.bang }; bang[ten] = moi; }
  }
  return bang ? { ...goi, bang } : goi;
}

/** Đọc 1 giá trị cấu hình (khối cau_hinh) — không có ⇒ mặc định. */
export function docCauHinh<T>(goi: Pick<GoiNen, "bang"> | undefined, khoa: string, macDinh: T): T {
  const k = goi?.bang.cau_hinh;
  if (!k) return macDinh;
  const r = k.rows.find((x) => x[0] === khoa);
  return r === undefined || r[1] === null || r[1] === undefined ? macDinh : (r[1] as T);
}
